import {
  PaginationButtonsProps,
  PaginationProps,
  SortDirection,
  Page
} from '@touche-lts/panel-ui/dist'
import { action, computed, observable, makeObservable } from 'mobx'
import { computedFn } from 'mobx-utils'
import { FilterRanges, TriggerSearchFunc } from './pagination-search-base.types'

export class PaginationSearchBase<SortProps, SearchProps, SearchResult> {
  private initialPaginationProps: PaginationProps<SortProps>
  private initialSearchProps: SearchProps
  private initialSearchResult: SearchResult[]
  public timeoutId: NodeJS.Timeout
  public needTimeoutProps: Array<keyof SearchProps>
  public triggerSearch: TriggerSearchFunc
  public userId: number | null = null;

  constructor(args: {
    initialPaginationProps: PaginationProps<SortProps>
    initialSearchProps: SearchProps
    initialSearchResult?: SearchResult[]
    needTimeoutProps?: Array<keyof SearchProps>
  }) {
    this.initialPaginationProps = args.initialPaginationProps
    this.initialSearchProps = args.initialSearchProps
    this.initialSearchResult = args.initialSearchResult || []
    this.needTimeoutProps = args.needTimeoutProps || []

    this.pagination = args.initialPaginationProps
    this.searchProps = args.initialSearchProps
    this.searchResults = args.initialSearchResult || []

    makeObservable(this, {
      timeoutId: false,
      needTimeoutProps: false,
      triggerSearch: false,
      searchResults: observable,
      pagination: observable,
      searchProps: observable,
      filterRanges: observable,
      processing: observable,
      setFilterRanges: action,
      setSearchResults: action,
      setPaginationProps: action,
      setSearchProps: action,
      setProcessing: action,
      areFilterRangesSet: computed,
      paginationInfo: computed,
      isColumnMarked: observable,
      getSortDirection: observable
    })
  }

  // OBSERVABLES
  public searchResults: SearchResult[]
  public pagination: PaginationProps<SortProps>
  public searchProps: SearchProps
  public filterRanges: FilterRanges = null
  public processing: boolean = true
  public catalogElementsTotalCount: number = null

  // ACTIONS
  public setFilterRanges = (filterRanges: FilterRanges) => {
    this.filterRanges = filterRanges
  }

  public setSearchResults = (searchResults: SearchResult[]) => {
    this.searchResults = searchResults
  }

  public setPaginationProps = (props: Partial<PaginationProps<SortProps>>) => {
    this.pagination = {
      ...this.pagination,
      ...props
    }
  }

  public setSearchProps = (newProps: Partial<SearchProps>) => {
    this.searchProps = {
      ...this.searchProps,
      ...newProps
    }
  }

  public setProcessing = (value: boolean) => {
    this.processing = value
  }

  private setCatalogElementsTotalCount = (value: number): void => {
    this.catalogElementsTotalCount = value
  }

  // METHODS
  public clearPaginationSearchBaseStore = (): void => {
    this.setSearchResults([])
    this.setPaginationProps(this.initialPaginationProps)
    this.setSearchProps(this.initialSearchProps)
    this.setSearchResults(this.initialSearchResult)
    this.setCatalogElementsTotalCount(null)
  }

  public handleSearchResultPaginationProps = (data: Page<any>) => {
    this.setPaginationProps({
      page: data.number,
      size: data.size,
      totalElements: data.totalElements,
      totalPages: data.totalPages
    })
    if (this.catalogElementsTotalCount === null) {
      this.setCatalogElementsTotalCount(data.totalElements)
    }
  }

  public onSearchPropsChange = (newProps: Partial<SearchProps>) => {
    this.setSearchProps(newProps)

    if (this.timeoutId) {
      clearTimeout(this.timeoutId)
    }

    if (this.needTimeoutProps.includes(Object.keys(newProps)[0] as any)) {
      this.timeoutId = setTimeout(
        () => this.triggerSearch({ resetPageNumber: true }),
        800
      )
    } else {
      this.triggerSearch({ resetPageNumber: true })
    }
  }

  public onPageSelect = (pageNumber: number) => {
    this.setPaginationProps({ page: pageNumber })
    this.triggerSearch()
  }

  public handleSortDirectionChange = (sortProp: SortProps) => (
    sortDirection: SortDirection
  ) => {
    this.setPaginationProps({
      sortProp,
      sortDirection
    })
    this.triggerSearch({ resetPageNumber: true })
  }

  // COMPUTED
  public get areFilterRangesSet(): boolean {
    return this.filterRanges !== null
  }

  public get paginationInfo(): PaginationButtonsProps {
    return {
      elementsPerPage: this.pagination.size,
      selectedPageNumber: this.pagination.page,
      totalElements: this.pagination.totalElements,
      totalPages: this.pagination.totalPages
    }
  }

  // @computed get resultsCount (): number {
  //   return this.searchResults.length
  // }

  // @computed get hasResults (): boolean {
  //   return this.resultsCount > 0
  // }

  public isColumnMarked = computedFn((sortProp: SortProps): boolean => {
    return this.pagination.sortProp === sortProp
  })

  public getSortDirection = computedFn(
    (sortProp: SortProps): SortDirection => {
      return this.isColumnMarked(sortProp)
        ? this.pagination.sortDirection
        : SortDirection.ASC
    }
  )
}
